import type {
  AdminRoundDetailResponse,
  AuthenticatedAdmin,
  RngChainVerification,
} from "@utopia-well-done-ltd/contracts";
import { useEffect, useRef, useState } from "react";
import { ApiError, api } from "../api/client.js";
import { can } from "../capabilities.js";
import { asAmount } from "../format.js";
import { ActivityTimeline } from "./ActivityTimeline.js";
import { BlackjackRound } from "./BlackjackRound.js";
import { CopyButton, detailUrl } from "./ExplorerTools.js";
import { PokerRound } from "./PokerRound.js";

export function RoundDetailModal({
  admin,
  onClose,
  roundId,
}: {
  admin: AuthenticatedAdmin;
  onClose: () => void;
  roundId: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [detail, setDetail] = useState<AdminRoundDetailResponse | null>(null);
  const [chain, setChain] = useState<RngChainVerification | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setDetail(null);
    setChain(null);
    setError(null);
    Promise.all([
      api.round(roundId, controller.signal),
      can(admin.role, "audit.read") ? api.rngChain(controller.signal) : Promise.resolve(null),
    ])
      .then(([found, rngChain]) => {
        setDetail(found);
        setChain(rngChain);
      })
      .catch((caught) => {
        if (!controller.signal.aborted)
          setError(caught instanceof ApiError ? caught.message : "Could not inspect the round.");
      });
    return () => controller.abort();
  }, [admin.role, roundId]);

  useEffect(() => {
    if (dialog.current?.showModal && !dialog.current.open) dialog.current.showModal();
    else dialog.current?.setAttribute("open", "");
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, []);

  return (
    <dialog
      aria-labelledby="operations-round-detail-title"
      className="round-dialog round-detail"
      onCancel={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
      ref={dialog}
    >
      <div className="round-dialog-head">
        <div>
          <h2 className="heading-20" id="operations-round-detail-title">
            Round detail
          </h2>
          <p className="mono-12 faint">{roundId}</p>
        </div>
        <div className="round-dialog-actions">
          <button
            aria-label="Close round detail"
            className="btn btn-quiet btn-sm"
            onClick={onClose}
            type="button"
          >
            Close
          </button>
          <CopyButton label="Copy link" value={detailUrl("rounds", roundId)} />
        </div>
      </div>
      <div className="round-dialog-body">
        {error ? (
          <p className="banner" role="alert">
            {error}
          </p>
        ) : null}
        {!detail && !error ? <p className="empty">Loading round detail…</p> : null}
        {detail ? (
          <>
            <div className="round-integrity">
              <span className="round-integrity-item">
                Integrity{" "}
                <strong
                  className={`badge badge-${detail.verification.verified ? "active" : "banned"}`}
                >
                  {detail.verification.verified
                    ? "Verified"
                    : (detail.verification.reason ?? "Not verified")}
                </strong>
              </span>
              {chain && detail.round.game !== "poker" ? (
                <span className="round-integrity-item">
                  RNG chain{" "}
                  <strong className={`badge badge-${chain.intact ? "active" : "banned"}`}>
                    {chain.intact ? `Intact (${chain.checked})` : `Broken at ${chain.brokenAt}`}
                  </strong>
                </span>
              ) : null}
            </div>
            {detail.round.game === "blackjack" ? (
              <BlackjackRound terminalState={detail.round.terminalState} />
            ) : null}
            {detail.round.game === "poker" ? (
              <PokerRound terminalState={detail.round.terminalState} />
            ) : null}
            <details className="round-technical-details">
              <summary>Integrity and technical details</summary>
              <dl>
                <dt>Engine</dt>
                <dd>{detail.round.engineVersion}</dd>
                <dt>Ruleset</dt>
                <dd className="mono-12">{detail.round.rulesetId ?? "—"}</dd>
                <dt>RNG commitment</dt>
                <dd className="mono-12 break-all">{detail.rng?.seedCommit ?? "—"}</dd>
                <dt>RNG reveal</dt>
                <dd className="mono-12 break-all">{detail.rng?.seedReveal ?? "Not revealed"}</dd>
              </dl>
            </details>
            <div className="round-detail-section">
              <strong>Wagers</strong>
              {detail.wagers.map((wager) => (
                <div className="round-detail-row" key={`${wager.player.id}:${wager.player.seatId}`}>
                  <span>{wager.player.seatId ?? wager.player.email}</span>
                  <span className="mono-12">
                    {asAmount({ amount: wager.stake, currency: detail.round.currency })} →{" "}
                    {wager.payout === null
                      ? "open"
                      : asAmount({ amount: wager.payout, currency: detail.round.currency })}
                  </span>
                </div>
              ))}
            </div>
            <div className="round-detail-section">
              <strong>Settlements</strong>
              {detail.settlements.length ? (
                detail.settlements.map((entry) => (
                  <div className="round-detail-row" key={entry.id}>
                    <span>{entry.kind}</span>
                    <span className="mono-12">{entry.id}</span>
                  </div>
                ))
              ) : (
                <p className="empty">No settlement movements.</p>
              )}
            </div>
            <div className="round-detail-section">
              <strong>Actions</strong>
              {detail.actions.length ? (
                detail.actions.map((action) => (
                  <div className="round-detail-row" key={action.sequence}>
                    <span>
                      #{action.sequence} {action.action}
                    </span>
                    <span>
                      {action.accepted === false ? "Rejected" : (action.seatId ?? "System")}
                    </span>
                  </div>
                ))
              ) : (
                <p className="empty">No actions recorded.</p>
              )}
            </div>
            {can(admin.role, "audit.read") ? <ActivityTimeline targetId={detail.round.id} /> : null}
          </>
        ) : null}
      </div>
    </dialog>
  );
}
